import classNames from 'classnames';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { AppName } from '../../apps/definition';
import { SidebarRoutes } from '../../containers/SidebarContainer';
import { Prompt } from '../../models/Prompts';
import { useQAController } from '../../scripts/QAController';
import { invokeFastApi } from '../../scripts/apis/fastapi';
import { useSortedInstantApps } from '../../scripts/hooks/sortedInstantApps';
import { logError } from '../../scripts/utils';
import { UIIcon } from '../controls/ui/UIIcon/UIIcon';

interface IRecommendedWorkflows {
  recommendedWorkflows: Prompt[];
}

export const RecommendedWorkflows: React.FC<IRecommendedWorkflows> = ({
  recommendedWorkflows,
}) => {
  const history = useHistory();
  const qaController = useQAController();
  const { sortedInstantConnectedApps } = useSortedInstantApps();

  const handleUseButtonClick = useCallback(
    ({
      promptId,
      template,
      searchFilters,
    }: {
      promptId: string;
      template: string;
      searchFilters: AppName[];
    }) => {
      try {
        invokeFastApi({
          path: `/prompts/prompt_used/${promptId}`,
          method: 'POST',
        });
      } catch (error) {
        logError(error);
      }

      let baseLlm = false;
      if (searchFilters[0] === 'all') {
        searchFilters = sortedInstantConnectedApps.map((app) => app.appName);
      }

      if (searchFilters[0] === 'baseLlm') {
        searchFilters = [];
        baseLlm = true;
      }

      history.push({
        pathname: SidebarRoutes.JitQA,
        state: { template, searchFilters, baseLlm },
      });

      qaController.triggerEvent('usePromptTemplate', {
        baseLlm,
        promptTemplate: template,
        searchFilters,
      });
    },
    [history, sortedInstantConnectedApps, qaController]
  );

  return (
    <div
      className={classNames('group relative flex items-center h-[38px]', {
        invisible: recommendedWorkflows.length === 0,
      })}
    >
      <div
        className="sm:flex hidden opacity-0 group-hover:opacity-40 absolute top-3 -left-5 bg-gray-20 rounded border-0 cursor-pointer"
        onClick={() => {
          document
            .getElementById('scrollContainer')
            ?.scrollBy({ left: -150, behavior: 'smooth' });
        }}
      >
        <UIIcon name="arrow-left" />
      </div>
      <div
        className="flex items-center gap-2 overflow-x-auto whitespace-nowrap no-scrollbar max-w-[750px]"
        id="scrollContainer"
      >
        {recommendedWorkflows.slice(0, 8).map((workflow) => (
          <div
            // eslint-disable-next-line max-len
            className="flex justify-between items-center py-1.5 px-3 min-w-[245px] neumorphic-background rounded cursor-pointer hover:bg-none hover:bg-purple-10 hover:border-purple-30 border border-solid border-gray-30 text-sm"
            key={workflow.id}
            onClick={() => {
              handleUseButtonClick({
                promptId: workflow.id,
                template: workflow.template,
                searchFilters: workflow.search_filters,
              });
            }}
          >
            <div className="max-w-[180px] whitespace-nowrap overflow-hidden truncate">
              {workflow.title}
            </div>
            <UIIcon name="arrow-up-right" stroke />
          </div>
        ))}
      </div>
      <div
        className="sm:flex hidden opacity-0 group-hover:opacity-40 absolute top-3 -right-1 bg-gray-20 rounded border-0 cursor-pointer"
        onClick={() => {
          document
            .getElementById('scrollContainer')
            ?.scrollBy({ left: 150, behavior: 'smooth' });
        }}
      >
        <UIIcon name="arrow-right" />
      </div>
    </div>
  );
};
